import React, { useState, useEffect, useRef } from 'react';
import { getWebSocketUrl } from '../../config/websocket';
import './VoiceChat.scss';

/**
 * Голосовой чат с WebRTC для комнат сообщества
 */
const VoiceChat = ({ roomSlug, currentUser }) => {
  const [ws, setWs] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const peersRef = useRef({});
  const localStreamRef = useRef(null);

  useEffect(() => {
    if (!roomSlug || !currentUser) {
      console.warn('VoiceChat: Missing roomSlug or currentUser', { roomSlug, currentUser });
      return;
    }

    console.log('VoiceChat useEffect - roomSlug:', roomSlug, 'currentUser:', currentUser);

    // Подключение к WebSocket
    const wsUrl = getWebSocketUrl(`ws/voice/${roomSlug}/`);
    console.log('Connecting to Voice WebSocket:', wsUrl);
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('✅ Voice chat connected successfully');
      setIsConnected(true);
      initializeMedia();
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleSignal(data);
    };

    websocket.onclose = () => {
      console.log('Voice chat disconnected');
      setIsConnected(false);
      cleanup();
    };

    websocket.onerror = (error) => {
      console.error('Voice WebSocket error:', error);
    };

    setWs(websocket);

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
      cleanup();
    };
  }, [roomSlug, currentUser]);

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setLocalStream(stream);
      localStreamRef.current = stream;

      // Уведомляем других о присоединении
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          action: 'join',
          data: { user: currentUser.username }
        }));
      }
    } catch (error) {
      console.error('Failed to get media stream:', error);
      alert('Не удалось получить доступ к микрофону. Проверьте разрешения.');
    }
  };

  const handleSignal = async (data) => {
    const { action, from, data: payload } = data;

    switch (action) {
      case 'join':
        // Новый участник присоединился
        if (from !== currentUser.username) {
          setParticipants(prev => [...prev.filter(p => p !== from), from]);
          // Создаем offer для нового участника
          createPeerConnection(from, true);
        }
        break;

      case 'offer':
        // Получили offer от другого участника
        if (from !== currentUser.username) {
          await handleOffer(from, payload);
        }
        break;

      case 'answer':
        // Получили answer
        if (from !== currentUser.username) {
          await handleAnswer(from, payload);
        }
        break;

      case 'ice':
        // Получили ICE candidate
        if (from !== currentUser.username) {
          await handleIceCandidate(from, payload);
        }
        break;

      case 'leave':
        // Участник покинул чат
        setParticipants(prev => prev.filter(p => p !== from));
        if (peersRef.current[from]) {
          peersRef.current[from].close();
          delete peersRef.current[from];
        }
        break;

      default:
        break;
    }
  };

  const createPeerConnection = async (peerId, isInitiator) => {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    const peerConnection = new RTCPeerConnection(configuration);
    peersRef.current[peerId] = peerConnection;

    // Добавляем локальный аудиопоток
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStreamRef.current);
      });
    }

    // Обработка входящих треков
    peerConnection.ontrack = (event) => {
      const [remoteStream] = event.streams;
      playRemoteAudio(peerId, remoteStream);
    };

    // Обработка ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          action: 'ice',
          data: event.candidate
        }));
      }
    };

    // Если мы инициаторы, создаем offer
    if (isInitiator) {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          action: 'offer',
          data: offer
        }));
      }
    }
  };

  const handleOffer = async (peerId, offer) => {
    if (!peersRef.current[peerId]) {
      await createPeerConnection(peerId, false);
    }

    const peerConnection = peersRef.current[peerId];
    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        action: 'answer',
        data: answer
      }));
    }
  };

  const handleAnswer = async (peerId, answer) => {
    const peerConnection = peersRef.current[peerId];
    if (peerConnection) {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    }
  };

  const handleIceCandidate = async (peerId, candidate) => {
    const peerConnection = peersRef.current[peerId];
    if (peerConnection) {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  };

  const playRemoteAudio = (peerId, stream) => {
    let audioElement = document.getElementById(`audio-${peerId}`);
    if (!audioElement) {
      audioElement = document.createElement('audio');
      audioElement.id = `audio-${peerId}`;
      audioElement.autoplay = true;
      document.body.appendChild(audioElement);
    }
    audioElement.srcObject = stream;
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const leaveVoiceChat = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        action: 'leave',
        data: { user: currentUser.username }
      }));
      ws.close();
    }
    cleanup();
  };

  const cleanup = () => {
    // Останавливаем локальный поток
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    }

    // Закрываем все peer connections
    Object.values(peersRef.current).forEach(peer => peer.close());
    peersRef.current = {};

    // Удаляем аудио элементы
    participants.forEach(peerId => {
      const audioElement = document.getElementById(`audio-${peerId}`);
      if (audioElement) {
        audioElement.remove();
      }
    });

    setParticipants([]);
    setIsConnected(false);
  };

  return (
    <div className="voice-chat">
      <div className="voice-chat__header">
        <h3>Голосовой чат</h3>
        <div className={`voice-chat__status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? 'Подключено' : 'Отключено'}
        </div>
      </div>

      <div className="voice-chat__participants">
        <h4>Участники ({participants.length + 1})</h4>
        <ul>
          <li className="voice-chat__participant voice-chat__participant--self">
            <div className="voice-chat__avatar">{currentUser?.username?.[0]?.toUpperCase()}</div>
            <span className="voice-chat__username">{currentUser?.username} (Вы)</span>
            <div className={`voice-chat__mic-status ${isMuted ? 'muted' : 'active'}`}>
              {isMuted ? '🔇' : '🎤'}
            </div>
          </li>
          {participants.map(participant => (
            <li key={participant} className="voice-chat__participant">
              <div className="voice-chat__avatar">{participant[0]?.toUpperCase()}</div>
              <span className="voice-chat__username">{participant}</span>
              <div className="voice-chat__mic-status active">🎤</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="voice-chat__controls">
        <button
          onClick={toggleMute}
          className={`voice-chat__btn ${isMuted ? 'muted' : ''}`}
          disabled={!isConnected}
        >
          {isMuted ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
            </svg>
          )}
          <span>{isMuted ? 'Включить микрофон' : 'Выключить микрофон'}</span>
        </button>

        <button
          onClick={leaveVoiceChat}
          className="voice-chat__btn voice-chat__btn--leave"
          disabled={!isConnected}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
          </svg>
          <span>Покинуть</span>
        </button>
      </div>
    </div>
  );
};

export default VoiceChat;
